import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../utils/dateUtils';
import { useTags } from '../contexts/TagContext';
import { getApiEndpoint } from '../utils/apiConfig';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'tags'
  const [searchUser, setSearchUser] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { refreshTags } = useTags();
  
  // 標籤管理相關狀態
  const [tags, setTags] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({
    name: '',
    description: ''
  });
  const [draggedTagId, setDraggedTagId] = useState(null);

  // 統計數據相關狀態
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalArticles: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);

  // 網路測試相關狀態
  const [networkTest, setNetworkTest] = useState({
    ping: null,
    bandwidth: null,
    testing: false,
    error: null
  });

  // 檢查管理員權限
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
  }, [navigate]);

  // 獲取統計數據
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint('/api/admin/stats'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setStats(result.stats);
      } else {
        console.error('Failed to fetch stats:', result.message);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // 載入統計數據
  useEffect(() => {
    fetchStats();
  }, []);

  // 獲取管理員token
  const getAdminToken = () => {
    return localStorage.getItem('adminToken');
  };

  // 搜索使用者
  const handleSearchUser = async () => {
    if (!searchUser.trim()) {
      setError('請輸入名稱');
      return;
    }

    setLoading(true);
    setError('');
    setSearchResult(null);

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint(`/api/admin/user/${searchUser}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setSearchResult(result.user);
      } else {
        setError(result.message || '使用者不存在');
      }
    } catch (error) {
      console.error('Search user error:', error);
      setError('搜索使用者失敗');
    } finally {
      setLoading(false);
    }
  };

  // Ban/Unban 使用者
  const handleBanUser = async (userId, shouldBan) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint(`/api/admin/user/${userId}/ban`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ shouldBan })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        // 如果搜索結果中的使用者被操作，也要更新
        if (searchResult && searchResult.id === userId) {
          setSearchResult({
            ...searchResult,
            status: shouldBan ? 'banned' : 'normal'
          });
        }
      } else {
        setError(result.message || '操作失敗');
      }
    } catch (error) {
      console.error('Ban user error:', error);
      setError('操作失敗');
    } finally {
      setLoading(false);
    }
  };

  // 刪除投票文章
  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('確定要刪除這篇投票文章嗎？此操作不可撤銷。')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint(`/api/admin/article/${articleId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
      } else {
        setError(result.message || '刪除失敗');
      }
    } catch (error) {
      console.error('Delete article error:', error);
      setError('刪除失敗');
    } finally {
      setLoading(false);
    }
  };

  // 登出
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    navigate('/admin/login');
  };

  // ==================== 標籤管理功能 ====================
  
  // 獲取所有標籤
  const fetchTags = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint('/api/admin/tags?includeInactive=true'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        // 確保按 order 排序
        const sortedTags = result.tags.sort((a, b) => {
          const orderA = a.order !== undefined ? a.order : 999999;
          const orderB = b.order !== undefined ? b.order : 999999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return a.name.localeCompare(b.name);
        });
        setTags(sortedTags);
      } else {
        setError('獲取標籤列表失敗');
      }
    } catch (error) {
      console.error('Fetch tags error:', error);
      setError('獲取標籤列表失敗');
    }
  };

  // 載入標籤（當切換到標籤頁時）
  useEffect(() => {
    if (activeTab === 'tags') {
      fetchTags();
    }
  }, [activeTab]);

  // 打開創建/編輯標籤模態框
  const handleOpenTagModal = (tag = null) => {
    if (tag) {
      setEditingTag(tag);
      setTagForm({
        name: tag.name,
        description: tag.description || ''
      });
    } else {
      setEditingTag(null);
      setTagForm({
        name: '',
        description: ''
      });
    }
    setShowTagModal(true);
    setError('');
    setSuccess('');
  };

  // 關閉標籤模態框
  const handleCloseTagModal = () => {
    setShowTagModal(false);
    setEditingTag(null);
    setTagForm({
      name: '',
      description: ''
    });
  };

  // 創建/更新標籤
  const handleSaveTag = async () => {
    if (!tagForm.name.trim()) {
      setError('標籤名稱不能為空');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const url = editingTag 
        ? getApiEndpoint(`/api/admin/tags/${editingTag.id}`)
        : getApiEndpoint('/api/admin/tags');
      
      const method = editingTag ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tagForm)
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        handleCloseTagModal();
        fetchTags();
        // 刷新全局標籤快取
        refreshTags();
      } else {
        setError(result.message || '操作失敗');
      }
    } catch (error) {
      console.error('Save tag error:', error);
      setError('操作失敗');
    } finally {
      setLoading(false);
    }
  };

  // 切換標籤啟用狀態
  const handleToggleTagActive = async (tag) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint(`/api/admin/tags/${tag.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: !tag.isActive })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        fetchTags();
        // 刷新全局標籤快取
        refreshTags();
      } else {
        setError(result.message || '操作失敗');
      }
    } catch (error) {
      console.error('Toggle tag active error:', error);
      setError('操作失敗');
    } finally {
      setLoading(false);
    }
  };

  // 刪除標籤
  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('確定要刪除這個標籤嗎？如果有問題正在使用此標籤，將無法刪除。')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint(`/api/admin/tags/${tagId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        fetchTags();
        // 刷新全局標籤快取
        refreshTags();
      } else {
        setError(result.message || '刪除失敗');
      }
    } catch (error) {
      console.error('Delete tag error:', error);
      setError('刪除失敗');
    } finally {
      setLoading(false);
    }
  };

  // 拖拽開始
  const handleDragStart = (e, tagId) => {
    setDraggedTagId(tagId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', tagId);
    e.currentTarget.style.opacity = '0.5';
  };

  // 拖拽結束
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTagId(null);
  };

  // 拖拽懸停
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // 拖拽放置
  const handleDrop = async (e, targetTagId) => {
    e.preventDefault();
    
    if (!draggedTagId || draggedTagId === targetTagId) {
      return;
    }

    // 重新排序標籤
    const draggedIndex = tags.findIndex(t => t.id === draggedTagId);
    const targetIndex = tags.findIndex(t => t.id === targetTagId);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const newTags = [...tags];
    const [draggedTag] = newTags.splice(draggedIndex, 1);
    newTags.splice(targetIndex, 0, draggedTag);

    // 更新本地狀態（樂觀更新）
    setTags(newTags);

    // 準備更新順序的數據
    const tagOrders = newTags.map((tag, index) => ({
      tagId: tag.id,
      order: index
    }));

    // 發送到後端
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint('/api/admin/tags/reorder'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tagOrders })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess('標籤順序已更新');
        // 刷新全局標籤快取
        refreshTags();
      } else {
        setError(result.message || '更新順序失敗');
        // 恢復原順序
        fetchTags();
      }
    } catch (error) {
      console.error('Reorder tags error:', error);
      setError('更新順序失敗');
      // 恢復原順序
      fetchTags();
    } finally {
      setLoading(false);
      setDraggedTagId(null);
    }
  };

  // 網路測試功能
  const handlePingTest = async () => {
    setNetworkTest({ ...networkTest, testing: true, error: null, ping: null });
    
    try {
      const token = getAdminToken();
      const startTime = performance.now();
      
      // 使用GraphQL mutation進行ping測試
      const response = await fetch(getApiEndpoint('/graphql'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: `
            mutation {
              adminPingTest {
                success
                timestamp
                message
              }
            }
          `
        })
      });

      const endTime = performance.now();
      const result = await response.json();
      
      if (result.data && result.data.adminPingTest && result.data.adminPingTest.success) {
        const pingTime = Math.round(endTime - startTime);
        setNetworkTest({
          ...networkTest,
          ping: pingTime,
          testing: false,
          error: null
        });
      } else {
        throw new Error(result.errors?.[0]?.message || 'Ping測試失敗');
      }
    } catch (error) {
      console.error('Ping test error:', error);
      setNetworkTest({
        ...networkTest,
        testing: false,
        error: error.message || 'Ping測試失敗'
      });
    }
  };

  const handleBandwidthTest = async (size = '1MB') => {
    setNetworkTest({ ...networkTest, testing: true, error: null, bandwidth: null });
    
    try {
      const token = getAdminToken();
      const startTime = performance.now();
      
      const response = await fetch(getApiEndpoint(`/api/admin/bandwidth-test?size=${size}`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('帶寬測試失敗');
      }

      // 讀取響應數據
      const blob = await response.blob();
      const endTime = performance.now();
      
      const duration = (endTime - startTime) / 1000; // 轉換為秒
      const sizeBytes = blob.size;
      const sizeMB = sizeBytes / (1024 * 1024);
      const speedMbps = (sizeMB * 8) / duration; // 轉換為Mbps
      const speedMBps = sizeMB / duration; // MB/s
      
      setNetworkTest({
        ...networkTest,
        bandwidth: {
          size: size,
          sizeBytes: sizeBytes,
          duration: duration.toFixed(2),
          speedMbps: speedMbps.toFixed(2),
          speedMBps: speedMBps.toFixed(2)
        },
        testing: false,
        error: null
      });
    } catch (error) {
      console.error('Bandwidth test error:', error);
      setNetworkTest({
        ...networkTest,
        testing: false,
        error: error.message || '帶寬測試失敗'
      });
    }
  };

  // 更新使用者屬性
  const handleUpdateUserAttributes = async (userId, isSemiAdmin, isVerified) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = getAdminToken();
      const response = await fetch(getApiEndpoint(`/api/admin/user/${userId}/attributes`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isSemiAdmin, isVerified })
      });

      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        // 更新搜索結果
        if (searchResult && searchResult.id === userId) {
          setSearchResult({
            ...searchResult,
            isSemiAdmin: result.user.isSemiAdmin,
            isVerified: result.user.isVerified
          });
        }
      } else {
        setError(result.message || '更新失敗');
      }
    } catch (error) {
      console.error('Update user attributes error:', error);
      setError('更新失敗');
    } finally {
      setLoading(false);
    }
  };

  const UserCard = ({ user, showActions = true }) => {
    const [isSemiAdmin, setIsSemiAdmin] = useState(user.isSemiAdmin || false);
    const [isVerified, setIsVerified] = useState(user.isVerified || false);

    // 當user更新時同步狀態
    useEffect(() => {
      setIsSemiAdmin(user.isSemiAdmin || false);
      setIsVerified(user.isVerified || false);
    }, [user.isSemiAdmin, user.isVerified]);

    const handleToggleSemiAdmin = () => {
      const newValue = !isSemiAdmin;
      setIsSemiAdmin(newValue);
      handleUpdateUserAttributes(user.id, newValue, isVerified);
    };

    const handleToggleVerified = () => {
      const newValue = !isVerified;
      setIsVerified(newValue);
      handleUpdateUserAttributes(user.id, isSemiAdmin, newValue);
    };

    return (
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{user.username}</h3>
            <p className="text-gray-600">{user.email}</p>
            <div className="mt-2 space-y-1">
              <span className={`inline-block px-2 py-1 rounded text-xs ${
                user.status === 'banned' 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {user.status === 'banned' ? '已禁用' : '正常'}
              </span>
              <span className={`inline-block px-2 py-1 rounded text-xs ml-2 ${
                user.isActive 
                  ? 'bg-blue-100 text-blue-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {user.isActive ? '活躍' : '未啟用'}
              </span>
              {isSemiAdmin && (
                <span className="inline-block px-2 py-1 rounded text-xs ml-2 bg-purple-100 text-purple-800">
                  半管理員
                </span>
              )}
              {isVerified && (
                <span className="inline-block px-2 py-1 rounded text-xs ml-2 bg-yellow-100 text-yellow-800">
                  已驗證
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              最後登入: {user.lastLogin ? formatDateTime(user.lastLogin) : '從未登入'}
            </p>
            <p className="text-sm text-gray-500">
              註冊時間: {user.createdAt ? formatDateTime(user.createdAt) : '未知'}
            </p>
            
            {/* 屬性切換 */}
            {showActions && (
              <div className="mt-4 space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isSemiAdmin}
                    onChange={handleToggleSemiAdmin}
                    disabled={loading}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">半管理員</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isVerified}
                    onChange={handleToggleVerified}
                    disabled={loading}
                    className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                  />
                  <span className="text-sm text-gray-700">已驗證</span>
                </label>
              </div>
            )}
          </div>
          {showActions && (
            <div className="flex flex-col space-y-2 ml-4">
              <button
                onClick={() => handleBanUser(user.id, user.status !== 'banned')}
                disabled={loading}
                className={`px-3 py-1 rounded text-sm ${
                  user.status === 'banned'
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-red-500 text-white hover:bg-red-600'
                } disabled:opacity-50`}
              >
                {user.status === 'banned' ? '解除禁用' : '禁用'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-2xl font-bold text-gray-900">管理員儀表板</h1>
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              登出
            </button>
          </div>

          {/* 標籤頁切換 */}
          <div className="border-b border-gray-200 mt-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                使用者管理
              </button>
              <button
                onClick={() => setActiveTab('tags')}
                className={`${
                  activeTab === 'tags'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                標籤管理
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 消息提示 */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* 統計數據卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">總註冊人數</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {statsLoading ? '載入中...' : stats.totalUsers.toLocaleString()}
                </p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">總文章數</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {statsLoading ? '載入中...' : stats.totalArticles.toLocaleString()}
                </p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* 網路測試卡片 */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-semibold mb-4">Railway 網路測試</h2>
          
          {networkTest.error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {networkTest.error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ping測試 */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-3">Ping 測試</h3>
              <div className="space-y-3">
                <button
                  onClick={handlePingTest}
                  disabled={networkTest.testing}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {networkTest.testing ? '測試中...' : '開始 Ping 測試'}
                </button>
                {networkTest.ping !== null && (
                  <div className="mt-3 p-3 bg-blue-50 rounded">
                    <p className="text-sm text-gray-600">延遲時間</p>
                    <p className="text-2xl font-bold text-blue-600">{networkTest.ping} ms</p>
                  </div>
                )}
              </div>
            </div>

            {/* 帶寬測試 */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium mb-3">帶寬測試</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {['1KB', '10KB', '100KB', '1MB', '10MB'].map((size) => (
                    <button
                      key={size}
                      onClick={() => handleBandwidthTest(size)}
                      disabled={networkTest.testing}
                      className="bg-green-500 text-white px-3 py-2 rounded text-sm hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {networkTest.bandwidth && (
                  <div className="mt-3 p-3 bg-green-50 rounded space-y-2">
                    <div>
                      <p className="text-sm text-gray-600">測試大小</p>
                      <p className="text-lg font-semibold text-green-600">{networkTest.bandwidth.size}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">耗時</p>
                      <p className="text-lg font-semibold text-green-600">{networkTest.bandwidth.duration} 秒</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">下載速度</p>
                      <p className="text-2xl font-bold text-green-600">
                        {networkTest.bandwidth.speedMBps} MB/s
                      </p>
                      <p className="text-sm text-gray-500">
                        ({networkTest.bandwidth.speedMbps} Mbps)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 使用者管理標籤頁 */}
        {activeTab === 'users' && (
          <>
        {/* 搜索使用者 */}
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-semibold mb-4">搜索使用者</h2>
          <div className="flex space-x-4">
            <input
              type="text"
              placeholder="輸入名稱"
              value={searchUser}
              onChange={(e) => setSearchUser(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleSearchUser}
              disabled={loading}
              className="bg-indigo-500 text-white px-6 py-2 rounded hover:bg-indigo-600 disabled:opacity-50"
            >
              {loading ? '搜索中...' : '搜索'}
            </button>
          </div>
          
          {searchResult && (
            <div className="mt-4">
              <h3 className="font-medium mb-2">搜索結果:</h3>
              <UserCard user={searchResult} />
            </div>
          )}
        </div>

        {/* 刪除投票文章 */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">刪除投票文章</h2>
          <div className="flex space-x-4">
            <input
              type="text"
              placeholder="輸入文章ID"
              className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
              id="articleId"
            />
            <button
              onClick={() => {
                const articleId = document.getElementById('articleId').value;
                if (articleId) {
                  handleDeleteArticle(articleId);
                } else {
                  setError('請輸入文章ID');
                }
              }}
              disabled={loading}
              className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 disabled:opacity-50"
            >
              刪除文章
            </button>
          </div>
        </div>
          </>
        )}

        {/* 標籤管理標籤頁 */}
        {activeTab === 'tags' && (
          <>
            <div className="bg-white p-6 rounded-lg shadow mb-8">
          <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">標籤列表</h2>
            <button
                  onClick={() => handleOpenTagModal()}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
                  + 新增標籤
            </button>
          </div>
          
              {tags.length === 0 ? (
                <p className="text-gray-500 text-center py-8">暫無標籤，點擊上方按鈕創建第一個標籤</p>
              ) : (
                <div className="overflow-x-auto">
                  <p className="text-sm text-gray-600 mb-4">💡 提示：拖拽左側圖標可以調整標籤順序</p>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          順序
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          標籤名稱
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          描述
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          使用次數
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          狀態
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tags.map((tag, index) => (
                        <tr 
                          key={tag.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, tag.id)}
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, tag.id)}
                          className={`cursor-move ${draggedTagId === tag.id ? 'opacity-50' : ''} hover:bg-gray-50`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <svg 
                                className="w-5 h-5 text-gray-400" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                              </svg>
                              <span className="ml-2 text-sm text-gray-500">{index + 1}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{tag.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-500">{tag.description || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{tag.usageCount}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              tag.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {tag.isActive ? '啟用' : '停用'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleOpenTagModal(tag)}
                                disabled={loading}
                                className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50"
                              >
                                編輯
                              </button>
                              <button
                                onClick={() => handleToggleTagActive(tag)}
                                disabled={loading}
                                className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50"
                              >
                                {tag.isActive ? '停用' : '啟用'}
                              </button>
                              <button
                                onClick={() => handleDeleteTag(tag.id)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                刪除
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* 標籤模態框 */}
        {showTagModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-xl font-semibold mb-4">
                {editingTag ? '編輯標籤' : '新增標籤'}
              </h3>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    標籤名稱 *
                  </label>
                  <input
                    type="text"
                    value={tagForm.name}
                    onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例如：興趣愛好"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    標籤描述
                  </label>
                  <textarea
                    value={tagForm.description}
                    onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="標籤用途說明..."
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={handleCloseTagModal}
                  disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleSaveTag}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {loading ? '處理中...' : (editingTag ? '更新' : '創建')}
                </button>
              </div>
            </div>
          </div>
          )}
      </div>
    </div>
  );
};

export default AdminDashboard;
